if (!customElements.get('wk-handwriting-upload')) {
  customElements.define(
    'wk-handwriting-upload',
    class WkHandwritingUpload extends HTMLElement {
      static MAX_FILE_BYTES = 20 * 1024 * 1024;
      static ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf'];
      static ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

      connectedCallback() {
        this.input = this.querySelector('.wk-handwriting-upload__input');
        this.trigger = this.querySelector('[data-wk-handwriting-trigger]');
        this.filename = this.querySelector('[data-wk-handwriting-filename]');
        this.clearButton = this.querySelector('[data-wk-handwriting-clear]');
        this.errorEl = this.querySelector('.wk-handwriting-upload__error');
        this.downloadLink = this.querySelector('.wk-handwriting-upload__download');
        this.templatesData = this.querySelector('[data-wk-handwriting-templates]');

        if (!this.input || !this.trigger) return;

        this.placeholder = this.input.dataset.placeholder || this.filename.textContent.trim();

        this.onInputChange = this.onInputChange.bind(this);
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onClearClick = this.onClearClick.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        this.input.addEventListener('change', this.onInputChange);
        this.trigger.addEventListener('click', this.onTriggerClick);
        if (this.clearButton) {
          this.clearButton.addEventListener('click', this.onClearClick);
        }
        document.addEventListener('variant:change', this.onVariantChange);

        this.templates = this.parseTemplates();
        this.setFilenameDisplay('');
      }

      disconnectedCallback() {
        if (this.input) {
          this.input.removeEventListener('change', this.onInputChange);
        }
        if (this.trigger) {
          this.trigger.removeEventListener('click', this.onTriggerClick);
        }
        if (this.clearButton) {
          this.clearButton.removeEventListener('click', this.onClearClick);
        }
        document.removeEventListener('variant:change', this.onVariantChange);
      }

      parseTemplates() {
        if (!this.templatesData) return {};
        try {
          return JSON.parse(this.templatesData.textContent);
        } catch (error) {
          return {};
        }
      }

      onVariantChange(event) {
        const variant = event.detail && event.detail.variant;
        if (!variant || !this.downloadLink) return;

        const url = this.templates[String(variant.id)];
        if (url) {
          this.downloadLink.href = url;
          this.downloadLink.hidden = false;
          this.downloadLink.removeAttribute('tabindex');
          this.downloadLink.removeAttribute('aria-hidden');
        } else {
          this.downloadLink.removeAttribute('href');
          this.downloadLink.hidden = true;
          this.downloadLink.setAttribute('tabindex', '-1');
          this.downloadLink.setAttribute('aria-hidden', 'true');
        }
      }

      onTriggerClick(event) {
        event.preventDefault();
        this.input.click();
      }

      onClearClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.clearFile();
      }

      onInputChange() {
        const file = this.input.files && this.input.files[0];
        if (!file) {
          this.clearFile();
          return;
        }

        if (!this.isAllowedFile(file)) {
          this.showError(this.input.dataset.errorType);
          this.clearFile({ keepError: true });
          return;
        }

        if (file.size > WkHandwritingUpload.MAX_FILE_BYTES) {
          this.showError(this.input.dataset.errorSize);
          this.clearFile({ keepError: true });
          return;
        }

        this.clearError();
        this.input.setAttribute('name', this.input.dataset.propertyName);
        this.setFilenameDisplay(file.name);
      }

      isAllowedFile(file) {
        const extension = (file.name.split('.').pop() || '').toLowerCase();
        const hasAllowedExtension = WkHandwritingUpload.ALLOWED_EXTENSIONS.includes(extension);
        const hasAllowedMime = !file.type || WkHandwritingUpload.ALLOWED_MIME_TYPES.includes(file.type.toLowerCase());
        return hasAllowedExtension && hasAllowedMime;
      }

      setFilenameDisplay(name) {
        if (!this.filename) return;
        if (name) {
          this.filename.textContent = name;
          this.filename.classList.remove('is-placeholder');
          if (this.clearButton) this.clearButton.hidden = false;
        } else {
          this.filename.textContent = this.placeholder;
          this.filename.classList.add('is-placeholder');
          if (this.clearButton) this.clearButton.hidden = true;
        }
      }

      clearFile(options = {}) {
        this.input.value = '';
        this.input.removeAttribute('name');
        this.setFilenameDisplay('');
        if (!options.keepError) this.clearError();
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.hidden = !message;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }
    },
  );
}
